/**
 * Enter or replace the tenant's 8loop API key.
 *
 * API key only. Arun: *"make auth as api key and not creds. api key gives you all the
 * info you need."* The email/password mode this used to offer is gone, for his reason
 * plus two of its own: 8loop honours `Idempotency-Key` **only** on API-key auth, so
 * session auth can ring somebody twice after a crash with nothing to show it happened;
 * and a password field invites storing a real human's login in our database.
 *
 * It also used to hide behind a "Replace credential" button, which is how we ended up
 * with *"there isnt a way for me to add the api token for the individual accounts Eg.,
 * Supernova"* — the form existed and was unreachable in practice. An account with no key
 * now shows this form open, unprompted.
 *
 * The secret is write-only: the server returns a mask (`8l_live_abc…xyz`) and never the
 * value, so this form never pre-fills. Empty means "leave it alone", never "it is blank".
 */

import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage, type CredentialSummary } from "@/lib/api";
import { useUpdateAccount } from "@/lib/queries";

export function CredentialEditor({
  accountId,
  credentials,
  onDone,
}: {
  accountId: string;
  credentials: CredentialSummary;
  /** Omitted when the form is shown because no key exists — there is nothing to go back to. */
  onDone?: () => void;
}) {
  const update = useUpdateAccount(accountId);
  const [apiKey, setApiKey] = useState("");

  const trimmed = apiKey.trim();
  // 8loop keys are `8l_live_<id>.<secret>`. Checked here only to catch the two mistakes
  // that produce a confusing 401 later: pasting the key id alone, or pasting with the
  // surrounding quotes from a JSON file. Anything else is the server's to reject.
  const shapeWarning =
    trimmed.length === 0
      ? null
      : /^["']|["']$/.test(trimmed)
        ? "Looks like it still has quotes around it."
        : !trimmed.includes(".")
          ? "An 8loop key has two halves separated by a dot — this looks like only the first."
          : null;

  const save = () => {
    update.mutate(
      { api_key: trimmed },
      {
        onSuccess: () => {
          toast.success("API key saved. Press Test connection to prove it works.");
          setApiKey("");
          onDone?.();
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="space-y-1.5">
        <Label htmlFor="cred-api-key">
          {credentials.mode === "none" ? "8loop API key" : "Replace the 8loop API key"}
        </Label>
        <Input
          id="cred-api-key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && trimmed && !update.isPending) save();
          }}
          placeholder={credentials.api_key_masked ?? "8l_live_…"}
          className="font-mono text-xs"
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        {shapeWarning ? (
          <p className="text-[11px] text-warning">{shapeWarning}</p>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            From the tenant's own Settings → API keys, with the{" "}
            <code className="font-mono">INITIATE_CALLS</code> scope. Add{" "}
            <code className="font-mono">MANAGE_AGENTS</code> too and this app can also tell you
            which telephony provider each channel dials over, and which caller ID it shows.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        {onDone && (
          <Button variant="ghost" size="sm" onClick={onDone} disabled={update.isPending}>
            Cancel
          </Button>
        )}
        <Button size="sm" onClick={save} disabled={!trimmed || update.isPending}>
          {update.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <KeyRound className="size-3.5" />
          )}
          {credentials.mode === "none" ? "Save API key" : "Replace key"}
        </Button>
      </div>
    </div>
  );
}
